import { ConfigProvider, Menu, Tooltip, type GetProp } from "antd";
import { Conversations, type ConversationsProps } from "@ant-design/x";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/auth";
import BaseLayout from "./BaseLayout";
import { ADMIN_ROLE, SYSTEM_LOG_ROLES, userHasAnyRole } from "../../utils/user";
import MenuCollapsedIcon from "../../assets/opsmate/menu-collapsed.svg";

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface AdminNavItem {
  path: string;
  label: string;
  icon: string;
  group?: string;
}

const ADMIN_EXPANDED_GROUPS_STORAGE_KEY = "azopsmate_admin_expanded_groups";

function sanitizeExpandedGroups(groups: string[], allowedGroups: string[]) {
  const allowed = new Set(allowedGroups);
  return Array.from(new Set(groups.filter((group) => allowed.has(group))));
}

function readStoredExpandedGroups(allowedGroups: string[]) {
  try {
    const raw = localStorage.getItem(ADMIN_EXPANDED_GROUPS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? sanitizeExpandedGroups(parsed, allowedGroups) : [];
  } catch {
    return [];
  }
}

function writeStoredExpandedGroups(groups: string[]) {
  try {
    localStorage.setItem(ADMIN_EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(groups));
  } catch {
    // Ignore storage failures; the sidebar still works for the current page.
  }
}

const ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY = "azopsmate_admin_sidebar_collapsed";

function readStoredSidebarCollapsed() {
  try {
    return localStorage.getItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeStoredSidebarCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    // Ignore storage failures; the sidebar still works for the current session.
  }
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const adminConsoleGroup = t("layout.adminConsole");
  const settingsGroup = t("nav.settings");
  const logsGroup = "System Logs";
  const canAccessFullAdmin = userHasAnyRole(user?.roles, [ADMIN_ROLE]);
  const canAccessSystemLogs = userHasAnyRole(user?.roles, SYSTEM_LOG_ROLES);
  const collapsibleGroups = useMemo(
    () =>
      canAccessFullAdmin ? [settingsGroup, logsGroup] : canAccessSystemLogs ? [logsGroup] : [],
    [canAccessFullAdmin, canAccessSystemLogs, logsGroup, settingsGroup],
  );
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    const stored = readStoredExpandedGroups(collapsibleGroups);
    if (stored.length > 0) return stored;
    return !canAccessFullAdmin && canAccessSystemLogs ? [logsGroup] : [];
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => readStoredSidebarCollapsed());

  const navEntries = useMemo<AdminNavItem[]>(() => {
    const entries: AdminNavItem[] = [
      {
        path: "/admin/analytics",
        label: t("nav.analytics"),
        icon: "ri-bar-chart-2-line",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/recommended-questions",
        label: t("nav.recommendations"),
        icon: "ri-list-check-3",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/feedback",
        label: t("nav.feedback"),
        icon: "ri-feedback-line",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/users",
        label: t("nav.userPermissions"),
        icon: "ri-user-settings-line",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/settings/general-source",
        label: t("nav.pathSettings"),
        icon: "ri-links-line",
        group: settingsGroup,
      },
      {
        path: "/admin/settings/study-source",
        label: t("nav.studySource"),
        icon: "ri-book-2-line",
        group: settingsGroup,
      },
      {
        path: "/admin/document-categories",
        label: "Doc Categories",
        icon: "ri-folder-line",
      },
      {
        path: "/admin/document-tags",
        label: "Doc Tags",
        icon: "ri-price-tag-3-line",
      },
      {
        path: "/admin/logs/update-logs",
        label: t("nav.updateLogs"),
        icon: "ri-upload-cloud-line",
        group: logsGroup,
      },
      {
        path: "/admin/logs/operation-logs",
        label: "Operation Logs",
        icon: "ri-list-check-2",
        group: logsGroup,
      },
      {
        path: "/admin/logs/ask-history",
        label: "Ask History",
        icon: "ri-brain-line",
        group: logsGroup,
      },
      ...(canAccessFullAdmin
        ? [
            {
              path: "/admin/logs/rag-sync-logs",
              label: "RAG Sync Logs",
              icon: "ri-refresh-line",
              group: logsGroup,
            },
          ]
        : []),
      {
        path: "/admin/health-monitor",
        label: "Health Monitor",
        icon: "ri-heart-pulse-line",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/security-records",
        label: "Security Records",
        icon: "ri-shield-keyhole-line",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/studies",
        label: "Studies",
        icon: "ri-book-line",
        group: adminConsoleGroup,
      },
      {
        path: "/admin/settings/system-settings",
        label: "Settings",
        icon: "ri-settings-3-line",
      },
    ];

    if (canAccessFullAdmin) return entries;
    if (canAccessSystemLogs) return entries.filter((entry) => entry.group === logsGroup);
    return [];
  }, [adminConsoleGroup, canAccessFullAdmin, canAccessSystemLogs, settingsGroup, logsGroup, t]);

  const conversationItems = useMemo<GetProp<ConversationsProps, "items">>(
    () =>
      navEntries.map((entry) => ({
        key: entry.path,
        label: (
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-5 w-5 items-center justify-center text-inherit">
              <i className={`${entry.icon} text-base`} aria-hidden />
            </span>
            <span className="truncate">{entry.label}</span>
          </span>
        ),
        group: entry.group,
      })),
    [navEntries],
  );

  // 与 Conversations 一致：分组按首次出现排序，无分组菜单保持独立位置。
  const orderedNavEntries = useMemo<AdminNavItem[]>(() => {
    const groups: AdminNavItem[][] = [];
    const groupMap = new Map<string, AdminNavItem[]>();
    for (const entry of navEntries) {
      if (!entry.group) {
        groups.push([entry]);
        continue;
      }
      if (!groupMap.has(entry.group)) {
        const group: AdminNavItem[] = [];
        groupMap.set(entry.group, group);
        groups.push(group);
      }
      groupMap.get(entry.group)!.push(entry);
    }
    return groups.flat();
  }, [navEntries]);

  const activeKey = useMemo(
    () =>
      navEntries.find((entry) => {
        if (entry.path === "/") return location.pathname === "/";
        return location.pathname === entry.path || location.pathname.startsWith(entry.path + "/");
      })?.path,
    [location.pathname, navEntries],
  );

  const activeEntry = useMemo(
    () => navEntries.find((entry) => entry.path === activeKey),
    [activeKey, navEntries],
  );

  useEffect(() => {
    if (activeEntry?.group !== settingsGroup && activeEntry?.group !== logsGroup) return;

    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.add(activeEntry.group);
      return Array.from(next);
    });
  }, [activeEntry?.group, location.pathname, logsGroup, settingsGroup]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = sanitizeExpandedGroups(prev, collapsibleGroups);
      if (next.length === prev.length && next.every((group, index) => group === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [collapsibleGroups]);

  useEffect(() => {
    writeStoredExpandedGroups(expandedGroups);
  }, [expandedGroups]);

  useEffect(() => {
    writeStoredSidebarCollapsed(collapsed);
  }, [collapsed]);

  const handleExpandGroups = useCallback((nextKeys: string[]) => {
    setExpandedGroups((prev) => {
      const prevSet = new Set(prev);
      const addedKeys = nextKeys.filter((key) => !prevSet.has(key));
      if (addedKeys.length === 0) return nextKeys;
      return Array.from(new Set([...prev, ...addedKeys]));
    });
  }, []);

  const groupableConfig = useMemo<ConversationsProps["groupable"]>(
    () => ({
      label: (group) => (
        <span
          className="flex min-w-0 flex-1 items-center gap-3"
          style={{
            width: "100%",
            fontSize: 14,
            fontWeight: 400,
            color:
              group === settingsGroup || group === logsGroup ? "rgba(0, 0, 0, 0.88)" : "#666666",
          }}
        >
          {group === settingsGroup ? (
            <span className="flex h-5 w-5 items-center justify-center text-inherit">
              <i className="ri-settings-4-line text-base" aria-hidden />
            </span>
          ) : null}
          {group === logsGroup ? (
            <span className="flex h-5 w-5 items-center justify-center text-inherit">
              <i className="ri-file-list-3-line text-base" aria-hidden />
            </span>
          ) : null}
          <span
            className="min-w-0 flex-1 truncate"
            style={group === adminConsoleGroup ? { fontWeight: 600, color: "rgba(0,0,0,.88)" } : {}}
          >
            {group}
          </span>
          {group === adminConsoleGroup ? (
            <Tooltip title={t("common.collapse")} placement="right">
              <img
                src={MenuCollapsedIcon}
                alt=""
                className="cursor-pointer"
                style={{ flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed(true);
                }}
              />
            </Tooltip>
          ) : null}
        </span>
      ),
      expandedKeys: expandedGroups,
      onExpand: handleExpandGroups,
      collapsible: (group) => group === settingsGroup || group === logsGroup,
    }),
    [adminConsoleGroup, expandedGroups, handleExpandGroups, logsGroup, settingsGroup, t],
  );

  return (
    <BaseLayout>
      {/* Body: Sidebar + Content */}
      <div className="admin-layout flex flex-1 min-h-0" style={{ minHeight: "100vh" }}>
        {/* Left Sidebar */}
        <aside
          className={`flex-shrink-0 bg-[#f8f8f8] border-r border-gray-200 flex flex-col overflow-hidden transition-[width] duration-200 ease-in-out ${
            collapsed ? "w-12" : "w-56"
          }`}
        >
          <ConfigProvider
            theme={{
              token: { colorPrimary: "#830050", borderRadiusLG: 4 },
              components: { Menu: { collapsedWidth: 40, itemSelectedBg: "rgba(131, 0, 81, 0.1)" } },
            }}
          >
            <div
              className={`flex-1 min-h-0 overflow-y-auto ${collapsed ? "px-1 py-2" : "px-3 py-4"}`}
            >
              {collapsed ? (
                <>
                  <div className="mb-1 flex justify-center py-1">
                    <Tooltip title={t("common.expand")} placement="right">
                      <img
                        src={MenuCollapsedIcon}
                        alt=""
                        className="cursor-pointer"
                        onClick={() => setCollapsed(false)}
                      />
                    </Tooltip>
                  </div>
                  <Menu
                    mode="inline"
                    inlineCollapsed
                    selectedKeys={activeKey ? [activeKey] : []}
                    onClick={({ key }) => navigate(key)}
                    items={orderedNavEntries.map((entry) => ({
                      key: entry.path,
                      icon: <i className={`${entry.icon} text-base`} aria-hidden />,
                      label: entry.label,
                    }))}
                    style={{ border: "none", background: "transparent" }}
                  />
                </>
              ) : (
                <Conversations
                  activeKey={activeKey}
                  rootClassName="admin-sidebar-conversations"
                  onActiveChange={(key) => navigate(String(key))}
                  items={conversationItems}
                  groupable={groupableConfig}
                  styles={{
                    root: { padding: "0px", background: "transparent" },
                    item: { borderRadius: 8 },
                  }}
                />
              )}
            </div>
          </ConfigProvider>

          {!collapsed && (
            <div className="px-5 py-4 border-t border-gray-100">
              <span className="text-xs text-gray-300 whitespace-nowrap">v1.0.0</span>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-white">
          <div className="p-8">{children}</div>
        </main>
      </div>
    </BaseLayout>
  );
}
